import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  IconButton,
  Chip,
  alpha,
  Grid,
  Divider,
  Container,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ColorizeIcon from '@mui/icons-material/Colorize';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import PaletteIcon from '@mui/icons-material/Palette';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import MouseIcon from '@mui/icons-material/Mouse';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeStyles, PRIMARY_PRESET_COLORS, SECONDARY_PRESET_COLORS } from '../theme';

const ColorSelection = ({ color, onChange, presetColors, title }) => {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
        {title} Preset Colors
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
        {presetColors.map((preset) => (
          <Box
            key={preset.value}
            onClick={() => onChange(preset.value)}
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: preset.value,
              cursor: 'pointer',
              border: '3px solid',
              borderColor: color === preset.value ? 'white' : 'transparent',
              boxShadow: color === preset.value 
                ? `0 0 0 2px ${preset.value}, 0 4px 8px ${alpha(preset.value, 0.4)}`
                : '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(preset.value, 0.4)}`,
              },
            }}
          />
        ))}
      </Box>
      
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
        Custom {title} Color
      </Typography>
      <Paper
        elevation={0}
        sx={{ 
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: color,
              boxShadow: `0 2px 8px ${alpha(color, 0.3)}`,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pick a custom {title.toLowerCase()} color
            </Typography>
            <input
              type="color"
              value={color}
              onChange={(e) => onChange(e.target.value)}
              style={{
                width: '100%',
                height: 40,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const ButtonPreview = ({ primaryColor, secondaryColor }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="subtitle2" sx={{ mb: 2 }}>
      Buttons
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <Button
        variant="contained"
        sx={(theme) => ThemeStyles.button(theme, 'contained', primaryColor)}
      >
        Primary
      </Button>
      <Button
        variant="contained"
        color="secondary"
        sx={(theme) => ThemeStyles.button(theme, 'contained', secondaryColor)}
      >
        Secondary
      </Button>
      <Button
        variant="outlined"
        sx={(theme) => ThemeStyles.button(theme, 'outlined', primaryColor)}
      >
        Outlined
      </Button>
    </Box>
  </Box>
);

const ChipsPreview = ({ primaryColor, secondaryColor }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="subtitle2" sx={{ mb: 2 }}>
      Chips
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        label="Primary"
        color="primary"
        sx={{ 
          bgcolor: primaryColor,
          '&:hover': { bgcolor: alpha(primaryColor, 0.8) }
        }}
      />
      <Chip
        label="Secondary"
        color="secondary"
        sx={{ 
          bgcolor: secondaryColor,
          '&:hover': { bgcolor: alpha(secondaryColor, 0.8) }
        }}
      />
      <Chip
        label="Outlined"
        variant="outlined"
        sx={{ 
          borderColor: alpha(primaryColor, 0.5),
          color: primaryColor,
          '&:hover': { 
            borderColor: primaryColor,
            bgcolor: alpha(primaryColor, 0.08)
          }
        }}
      />
    </Box>
  </Box>
);

const CardPreview = ({ primaryColor, secondaryColor }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="subtitle2" sx={{ mb: 2 }}>
      Card Interaction
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: alpha(primaryColor, 0.2),
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: primaryColor,
              bgcolor: alpha(primaryColor, 0.08)
            }
          }}
        >
          <Typography variant="body2">
            Primary
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={6}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: alpha(secondaryColor, 0.2),
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: secondaryColor,
              bgcolor: alpha(secondaryColor, 0.08)
            }
          }}
        >
          <Typography variant="body2">
            Secondary
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const AvatarPreview = ({ primaryColor, secondaryColor }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 2 }}>
      Avatars & Selection
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Avatar
        sx={{ 
          bgcolor: alpha(primaryColor, 0.1),
          color: primaryColor
        }}
      >
        <ColorizeIcon />
      </Avatar>
      <Avatar
        sx={{ 
          bgcolor: alpha(secondaryColor, 0.1),
          color: secondaryColor
        }}
      >
        <PaletteIcon />
      </Avatar>
      <Typography
        variant="body1"
        sx={{
          position: 'relative',
          '&::selection': {
            bgcolor: alpha(primaryColor, 0.2),
            color: 'inherit'
          }
        }}
      >
        Selected text color
      </Typography>
    </Box>
  </Box>
);

const PreviewSection = ({ primaryColor, secondaryColor, title }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ColorLensIcon sx={{ color: primaryColor }} />
        {title}
      </Typography>

      <ButtonPreview primaryColor={primaryColor} secondaryColor={secondaryColor} />
      <ChipsPreview primaryColor={primaryColor} secondaryColor={secondaryColor} />
      <CardPreview primaryColor={primaryColor} secondaryColor={secondaryColor} />
      <AvatarPreview primaryColor={primaryColor} secondaryColor={secondaryColor} />
    </Paper>
  );
};

const ThemeSettings = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const defaultPrimaryColor = '#FF4D4F';
  const defaultSecondaryColor = '#CE93D8'; // Light purple as default secondary color
  const navigate = useNavigate();
  
  const [primaryColor, setPrimaryColor] = useState(user?.themePreferences?.primaryColor || defaultPrimaryColor);
  const [secondaryColor, setSecondaryColor] = useState(user?.themePreferences?.secondaryColor || defaultSecondaryColor);
  const [compactView, setCompactView] = useState(user?.themePreferences?.compactView || false);
  const [emojiCursor, setEmojiCursor] = useState(user?.themePreferences?.emojiCursor !== false); // Default to true if not set
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // Update emojiCursor state when user object changes
  useEffect(() => {
    if (user?.themePreferences?.emojiCursor !== undefined) {
      setEmojiCursor(user.themePreferences.emojiCursor !== false);
    }
  }, [user?.themePreferences?.emojiCursor]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        'themePreferences.primaryColor': primaryColor,
        'themePreferences.secondaryColor': secondaryColor,
        'themePreferences.compactView': compactView,
        'themePreferences.emojiCursor': emojiCursor,
        updatedAt: new Date(),
        updatedBy: user.email
      });
    } catch (error) {
      console.error('Error saving theme preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPrimaryColor(defaultPrimaryColor);
    setSecondaryColor(defaultSecondaryColor);
    setCompactView(false);
    setEmojiCursor(true);
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 2 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 2,
        mb: 3,
        mt: 2
      }}>
        <IconButton
          onClick={() => navigate('/app')}
          sx={{
            color: 'text.primary',
            '&:hover': {
              bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ 
          fontFamily: 'StrawberrySansDisplay',
          fontWeight: 500
        }}>
          Theme Settings
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          maxWidth: '1440px',
          width: '100%',
          mx: 'auto',
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: alpha(primaryColor, 0.1),
                color: primaryColor,
              }}
            >
              <ColorizeIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                bgcolor: alpha(secondaryColor, 0.1),
                color: secondaryColor,
                position: 'relative',
                top: 14,
                left: -24,
                border: '2px solid',
                borderColor: 'background.paper',
              }}
            >
              <PaletteIcon sx={{ fontSize: 24 }} />
            </Avatar>
          </Box>
          <Box sx={{ ml: -2 }}>
            <Typography variant="body1" color="text.secondary">
              Customize the application's appearance
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.default',
                height: '100%',
              }}
            >
              <Typography variant="h6" sx={{ mb: 3 }}>
                Choose Colors
              </Typography>
              
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab 
                  label="Primary" 
                  icon={<Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: primaryColor, mb: 0.5 }} />}
                  iconPosition="start"
                />
                <Tab 
                  label="Secondary" 
                  icon={<Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: secondaryColor, mb: 0.5 }} />}
                  iconPosition="start"
                />
              </Tabs>
              
              {activeTab === 0 && (
                <ColorSelection 
                  color={primaryColor} 
                  onChange={setPrimaryColor} 
                  presetColors={PRIMARY_PRESET_COLORS}
                  title="Primary"
                />
              )}
              
              {activeTab === 1 && (
                <ColorSelection 
                  color={secondaryColor} 
                  onChange={setSecondaryColor} 
                  presetColors={SECONDARY_PRESET_COLORS}
                  title="Secondary"
                />
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Layout Options
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={compactView}
                    onChange={(e) => setCompactView(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: primaryColor,
                        '&:hover': {
                          backgroundColor: alpha(primaryColor, 0.04),
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: primaryColor,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {compactView ? (
                      <ViewCompactIcon sx={{ fontSize: 20, color: primaryColor }} />
                    ) : (
                      <ViewComfyIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {compactView ? 'Compact View' : 'Regular View'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {compactView 
                          ? 'Optimize space usage in hotel details' 
                          : 'Use standard spacing for hotel details'
                        }
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                  }
                }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Cursor Options
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={emojiCursor}
                    onChange={(e) => setEmojiCursor(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: primaryColor,
                        '&:hover': {
                          backgroundColor: alpha(primaryColor, 0.04),
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: primaryColor,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {emojiCursor ? (
                      <EmojiEmotionsIcon sx={{ fontSize: 20, color: primaryColor }} />
                    ) : (
                      <MouseIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {emojiCursor ? 'Emoji Cursor' : 'Default Cursor'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {emojiCursor 
                          ? 'Use your profile emoji as the cursor' 
                          : 'Use the default system cursor'
                        }
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                  }
                }}
              />
              
              <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{ 
                    flex: 1,
                    bgcolor: primaryColor,
                    '&:hover': { bgcolor: alpha(primaryColor, 0.8) }
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={isSaving}
                  sx={{ 
                    borderColor: alpha(primaryColor, 0.5),
                    color: primaryColor,
                    '&:hover': { 
                      borderColor: primaryColor,
                      bgcolor: alpha(primaryColor, 0.08)
                    }
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 3,
              height: '100%',
            }}>
              <PreviewSection 
                primaryColor={theme.palette.primary.main} 
                secondaryColor={theme.palette.secondary.main} 
                title="Current Theme" 
              />
              <PreviewSection 
                primaryColor={primaryColor} 
                secondaryColor={secondaryColor} 
                title="New Theme" 
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ThemeSettings; 